import {
  EGI_APP_ACTIONS,
  EgiAppActions,
  NavigationItemActions
} from '@/app/platform/platform-data/actions/types';
import { ErrorInformation } from '@shared/models/error/error-information';

import { Inject, Injectable } from '@angular/core';
import { NAVIGATION_ITEM_SERVICE, NavigationItemService } from '@/app/platform/platform-data/services/types';
import { AsyncResult } from '@common/result/asyncResult';
import { EgiAppEntityStore } from '@/app/platform/platform-data/store/egi-app.entity.store';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';
import {
  EGI_APP_SYNCHRONIZER,
  EgiAppsSynchronizer,
  FaultyClickTroughConfiguration
} from '@/app/platform/platform-data/synchronizers/types';
import { environment } from '@/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class NavigationItemStoreActions implements NavigationItemActions {
  constructor(
    @Inject(EGI_APP_ACTIONS)
    private readonly egiAppActions: EgiAppActions,
    @Inject(NAVIGATION_ITEM_SERVICE)
    private readonly navigationItemService: NavigationItemService,
    @Inject(EGI_APP_SYNCHRONIZER)
    private readonly egiAppsSynchronizer: EgiAppsSynchronizer,
    private readonly egiAppStore: EgiAppEntityStore
  ) {}

  async synchronizeAll(): AsyncResult<
    EgiAppEntity[],
    ErrorInformation | ErrorInformation<FaultyClickTroughConfiguration[]>
  > {
    const result = await this.egiAppsSynchronizer.synchronizeApps();

    result.ifSuccess(apps => {
      apps.forEach(app => app.rootNavigationItem.sortDescendants());
      this.egiAppStore.set(apps);
    });
    return result;
  }

  async update(item: NavigationItemEntity): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.navigationItemService.changeName(item);
    result.ifSuccess(() => this.egiAppActions.reloadOne(item.appId));
    return result;
  }

  async createMenuItem(
    item: NavigationItemEntity
  ): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.navigationItemService.createMenuItem(item);
    result.ifSuccess(() => this.egiAppActions.reloadOne(item.appId));
    return result;
  }

  async move(item: NavigationItemEntity): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.navigationItemService.move(item);

    result.ifSuccess(() => {
      this.egiAppActions.reloadOne(item.appId);
    });
    return result;
  }

  async create(item: NavigationItemEntity): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.navigationItemService.create(item);

    await result.ifSuccess(async navigationItem => {
      const noSync = JSON.parse(localStorage.getItem('noSync'));
      if (!noSync && !environment.production) {
        await this.egiAppsSynchronizer.synchronizeApps();
      }
      this.egiAppActions.reloadOne(navigationItem.appId);
    });

    return result;
  }

  async delete(item: NavigationItemEntity): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.navigationItemService.delete(item);
    result.ifSuccess(() => this.egiAppActions.reloadOne(item.appId));
    return result;
  }

  async changeConfiguration(
    i: NavigationItemEntity
  ): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.navigationItemService.changeConfiguration(i);
    result.ifSuccess(() => {
      this.egiAppActions.reloadOne(i.appId);
    });

    return result;
  }
}
